/**
 * Custom Discounts Page Component
 * Phase 3 Frontend - CRUD interface for custom discounts
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Chip,
  CircularProgress,
  Alert,
  TableSortLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  useCustomDiscounts,
  useDeleteCustomDiscount,
} from '../../../hooks/useDiscounts';
import CustomDiscountModal from './CustomDiscountModal';
import { useProductCategories } from '../../../hooks/useProducts';
import { coverageFromRow, describeCoverage } from '../../../utils/discountCoverage';
import {
  CustomDiscount,
  DiscountScope,
  DiscountStatus,
  GetCustomDiscountsParams,
} from '../../../types/discount.types';

export default function CustomDiscountsPage() {
  // State
  const [filters, setFilters] = useState<GetCustomDiscountsParams>({
    status: undefined,
    skip: 0,
    take: 20,
    searchTerm: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<CustomDiscount | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<CustomDiscount | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Hooks
  const { data, isLoading, error } = useCustomDiscounts(filters);
  const deleteMutation = useDeleteCustomDiscount();
  // #625: the list carries the five columns but no label (/qa phase 4 I2): worded here, against
  // this school's category names (the same route permission as this page).
  const { data: categories = [], isSuccess: categoriesLoaded } = useProductCategories();
  const categoryNames = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);

  // Handlers
  const handleOpenCreate = () => {
    setEditingDiscount(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (discount: CustomDiscount) => {
    setEditingDiscount(discount);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingDiscount(null);
  };

  const handleOpenDeleteDialog = (discount: CustomDiscount) => {
    setDiscountToDelete(discount);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (discountToDelete) {
      try {
        await deleteMutation.mutateAsync(discountToDelete.id);
        setDeleteDialogOpen(false);
        setDiscountToDelete(null);
      } catch (error) {
        // Error is handled by hook snackbar
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, searchTerm: value, skip: 0 });
  };

  const handleStatusChange = (status: DiscountStatus | undefined) => {
    setFilters({ ...filters, status, skip: 0 });
  };

  const handlePageChange = (_: any, page: number) => {
    setFilters({ ...filters, skip: (page - 1) * filters.take! });
  };

  const handleSort = (column: 'name' | 'createdAt') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const totalPages = useMemo(() => {
    return Math.ceil((data?.total || 0) / (filters.take || 20));
  }, [data?.total, filters.take]);

  const currentPage = useMemo(() => {
    return Math.floor((filters.skip || 0) / (filters.take || 20)) + 1;
  }, [filters.skip, filters.take]);

  const scopeColorMap: Record<DiscountScope, 'default' | 'primary' | 'secondary'> = {
    [DiscountScope.FAMILY]: 'primary',
    [DiscountScope.STUDENT]: 'secondary',
    [DiscountScope.INVOICE_ITEM]: 'default',
  };

  const statusColorMap: Record<
    DiscountStatus,
    'success' | 'warning' | 'default'
  > = {
    [DiscountStatus.ACTIVE]: 'success',
    [DiscountStatus.INACTIVE]: 'warning',
    [DiscountStatus.ARCHIVED]: 'default',
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load custom discounts. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Custom Discounts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Discount
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <TextField
          label="Search"
          value={filters.searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by name..."
          variant="outlined"
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
          }}
        />

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            onChange={(e) => handleStatusChange((e.target.value || undefined) as any)}
            label="Status"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value={DiscountStatus.ACTIVE}>Active</MenuItem>
            <MenuItem value={DiscountStatus.INACTIVE}>Inactive</MenuItem>
            <MenuItem value={DiscountStatus.ARCHIVED}>Archived</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortOrder}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Covers</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data && data.discounts.length > 0 ? (
                  data.discounts.map((discount) => (
                    <TableRow key={discount.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            {discount.name}
                          </Typography>
                          {discount.description && (
                            <Typography variant="caption" color="text.secondary">
                              {discount.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={discount.scope}
                          size="small"
                          color={scopeColorMap[discount.scope]}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {discount.isPercent
                              ? `${discount.value}%`
                              : `$${discount.value}`}
                          </Typography>
                          {discount.isStackable && (
                            <Chip label="Stackable" size="small" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell data-testid="discount-covers">
                        <Typography variant="body2">
                          {/* A chosen category waits for the names rather than flashing "(deleted category)". */}
                          {categoriesLoaded || discount.coversAllProducts || (discount.coversCategoryIds ?? []).length === 0
                            ? describeCoverage(coverageFromRow(discount), categoryNames)
                            : '…'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={discount.status}
                          size="small"
                          color={statusColorMap[discount.status]}
                        />
                      </TableCell>
                      <TableCell>
                        {discount.CreatedBy ? (
                          <Typography variant="body2">
                            {discount.CreatedBy.firstName}{' '}
                            {discount.CreatedBy.lastName}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(discount)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(discount)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No custom discounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
              />
            </Box>
          )}

          {/* Results Info */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Showing {data?.discounts.length === 0 ? 0 : filters.skip! + 1} to{' '}
            {Math.min(
              (filters.skip || 0) + (filters.take || 20),
              data?.total || 0
            )}{' '}
            of {data?.total || 0} results
          </Typography>
        </>
      )}

      {/* Modal */}
      <CustomDiscountModal
        open={modalOpen}
        onClose={handleCloseModal}
        discount={editingDiscount}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Discount?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>"{discountToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending && <CircularProgress size={20} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
